import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Target, ArrowLeft, Lightbulb, CheckCircle, XCircle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScenarioMode() {
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalXP, setTotalXP] = useState(0);
  const [scenariosCompleted, setScenariosCompleted] = useState(0);

  useEffect(() => {
    loadScenario();
  }, []);

  const loadScenario = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/scenario/random`, {
        credentials: 'include'
      });
      const data = await response.json();
      setScenario(data);
      setLoading(false);
    } catch (error) {
      toast.error('Помилка завантаження сценарію');
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedOption === null) {
      toast.error('Оберіть рішення!');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/scenario/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          scenario_id: scenario.scenario_id,
          selected_option: selectedOption
        })
      });
      const data = await response.json();
      
      setResult(data);
      setSubmitted(true);
      setTotalXP(prev => prev + data.xp_earned);
      setScenariosCompleted(prev => prev + 1);
      
      if (data.correct) {
        toast.success(`Правильно! +${data.xp_earned} XP`);
      } else {
        toast.error('Неправильна відповідь. Аналізуйте пояснення!');
      }
    } catch (error) {
      toast.error('Помилка відправки відповіді');
    }
  };

  const nextScenario = () => {
    setSubmitted(false);
    setSelectedOption(null);
    setResult(null);
    setLoading(true);
    loadScenario();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <div className="flex items-center space-x-6">
                <div className="text-sm text-muted-foreground">
                  Завершено: {scenariosCompleted}
                </div>
                <div className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span className="text-lg font-bold text-foreground">+{totalXP} XP</span>
                </div>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-2xl">
              <div className="flex items-center space-x-3 mb-6">
                <Target className="w-8 h-8 text-secondary" />
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-foreground">{scenario.title}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{scenario.description}</p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                  scenario.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-400' :
                  scenario.difficulty === 'Advanced' ? 'bg-yellow-500/20 text-yellow-400' :
                  scenario.difficulty === 'Expert' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {scenario.difficulty}
                </span>
              </div>
              
              <div className="space-y-6">
                <div className="bg-muted/30 p-6 rounded-xl border-l-4 border-primary">
                  <h3 className="text-lg font-bold text-foreground mb-2">📋 Контекст</h3>
                  <p className="text-foreground leading-relaxed">{scenario.context}</p>
                </div>

                <div className="bg-destructive/10 p-6 rounded-xl border-l-4 border-destructive">
                  <h3 className="text-lg font-bold text-foreground mb-2">⚠️ Проблема</h3>
                  <p className="text-foreground leading-relaxed font-medium">{scenario.problem}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-foreground">Оберіть Рішення:</h3>
              {scenario.options.map((option, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: submitted ? 1 : 1.02 }}
                  whileTap={{ scale: submitted ? 1 : 0.98 }}
                  onClick={() => !submitted && setSelectedOption(index)}
                  disabled={submitted}
                  className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                    submitted
                      ? index === result?.correct_option
                        ? 'border-accent bg-accent/10'
                        : selectedOption === index
                          ? 'border-destructive bg-destructive/10'
                          : 'border-border/50 bg-card/30 opacity-60'
                      : selectedOption === index
                        ? 'border-secondary bg-secondary/10'
                        : 'border-border/50 bg-card/30 hover:border-secondary/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-foreground mb-2">{option.title}</h4>
                      <p className="text-muted-foreground">{option.description}</p>
                    </div>
                    {submitted && (
                      index === result?.correct_option ? (
                        <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 ml-4" />
                      ) : selectedOption === index ? (
                        <XCircle className="w-6 h-6 text-destructive flex-shrink-0 ml-4" />
                      ) : null
                    )}
                  </div>
                </motion.button>
              ))}
            </div>

            {!submitted ? (
              <div className="text-center">
                <Button
                  onClick={handleSubmit}
                  data-testid="submit-answer-button"
                  size="lg"
                  disabled={selectedOption === null}
                  className="bg-secondary hover:bg-secondary/90 text-white h-14 px-10 text-lg rounded-xl font-bold"
                >
                  Підтвердити Рішення
                </Button>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <div className={`p-8 rounded-2xl border-2 ${
                  result.correct
                    ? 'border-accent bg-accent/10'
                    : 'border-destructive bg-destructive/10'
                }`}>
                  <div className="flex items-start space-x-4 mb-4">
                    {result.correct ? (
                      <CheckCircle className="w-8 h-8 text-accent flex-shrink-0 mt-1" />
                    ) : (
                      <XCircle className="w-8 h-8 text-destructive flex-shrink-0 mt-1" />
                    )}
                    <div>
                      <h3 className="text-2xl font-bold text-foreground mb-3">
                        {result.correct ? '✅ Правильно! Відмінний аналіз!' : '❌ Неправильна Відповідь'}
                      </h3>
                      <div className="prose prose-invert max-w-none">
                        <p className="text-foreground leading-relaxed text-lg mb-4">{result.explanation}</p>
                        {result.ai_analysis && !result.correct && (
                          <div className="bg-muted/50 p-4 rounded-lg mt-4 border-l-4 border-primary">
                            <h4 className="text-lg font-bold text-foreground mb-2 flex items-center">
                              <Lightbulb className="w-5 h-5 mr-2 text-primary" />
                              AI Аналіз
                            </h4>
                            <p className="text-foreground leading-relaxed">{result.ai_analysis}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <Button
                    onClick={nextScenario}
                    data-testid="next-scenario-button"
                    size="lg"
                    className="bg-secondary hover:bg-secondary/90 text-white h-14 px-10 text-lg rounded-xl font-bold"
                  >
                    Наступний Сценарій
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}